import { type FC, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import dayjs from 'dayjs'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/Select'
import { AdminHeader } from '@/components/organisms/AdminHeader'
import { type AllCentralsConfigurationHistoryEvent } from '@/model/firestore/useAllCentralsConfigurationHistory'
import { ALL_CENTRALS_REGION_ID } from '@/pages/stats/ConfigurationHistoryPage'
import {
  type AllCentralsConfigurationHistoryStats,
  type ConfigurationType,
} from '@/pages/stats/useAllCentralsConfigurationHistoryStats'

import { type StatsSection } from './ConfigurationHistoryView'

export interface AllCentralsConfigurationHistoryViewProps {
  availableRegions: Array<{ regionId: string; regionName: string }>
  availableDates: Array<{ date: string; label: string }>
  selectedOperatingDate: string | undefined
  stats: AllCentralsConfigurationHistoryStats
  events: AllCentralsConfigurationHistoryEvent[]
  regionNames: Record<string, string>
  eveningStartTime: Date | undefined
  eveningEndTime: Date | undefined
  isLoading: boolean
  isLive: boolean
  error: Error | undefined
  selectedSection: StatsSection
  onSectionChange: (section: StatsSection) => void
  onRegionChange: (regionId: string | undefined) => void
  onDateChange: (operatingDate: string | undefined) => void
  onBackToPiloteClicked: () => void
}

// Colors for configuration types
const CONFIG_TYPE_COLORS: Record<ConfigurationType, string> = {
  assignment: '#3b82f6', // blue-500
  mobileRequests: '#22c55e', // green-500
  waitTime: '#f59e0b', // amber-500
  assistedAssignment: '#8b5cf6', // violet-500
}

// Colors for heatmap intensity
const HEATMAP_COLORS = [
  '#f0f9ff', // very light
  '#bae6fd', // light
  '#38bdf8', // medium
  '#0284c7', // dark
  '#075985', // very dark
]

const getHeatmapColor = (count: number, maxCount: number): string => {
  if (count === 0) return '#f3f4f6' // gray-100
  if (maxCount === 0) return HEATMAP_COLORS[0]
  const intensity = Math.min(Math.floor((count / maxCount) * HEATMAP_COLORS.length), HEATMAP_COLORS.length - 1)
  return HEATMAP_COLORS[intensity]
}

// Summary cards component
const SummaryCards: FC<{ stats: AllCentralsConfigurationHistoryStats }> = ({ stats }) => (
  <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-2xl font-bold text-gray-900">{stats.totalSwitches}</div>
      <div className="text-sm text-gray-500">
        <FormattedMessage id="allCentrals.totalSwitches" />
      </div>
    </div>
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-2xl font-bold text-gray-900">{stats.activeCentralsCount}</div>
      <div className="text-sm text-gray-500">
        <FormattedMessage id="allCentrals.activeCentrals" />
      </div>
    </div>
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-2xl font-bold text-gray-900">
        {stats.switchesByRegion.length > 0 ? stats.switchesByRegion[0].regionName : '-'}
      </div>
      <div className="text-sm text-gray-500">
        <FormattedMessage id="allCentrals.mostActiveCentral" />
      </div>
    </div>
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-2xl font-bold text-gray-900">
        {stats.totalSwitches === 0
          ? '-'
          : Object.entries(stats.switchesByConfigType).reduce(
              (max, [type, count]) =>
                count > (stats.switchesByConfigType[max as ConfigurationType] ?? 0) ? type : max,
              'assignment'
            )}
      </div>
      <div className="text-sm text-gray-500">
        <FormattedMessage id="allCentrals.mostChangedConfig" />
      </div>
    </div>
  </div>
)

// Bar chart for switches by config type
const SwitchesByTypeChart: FC<{ stats: AllCentralsConfigurationHistoryStats }> = ({ stats }) => {
  const intl = useIntl()

  const data = useMemo(() => {
    const typeLabels: Record<ConfigurationType, string> = {
      assignment: intl.formatMessage({ id: 'configurationHistory.types.assignment' }),
      mobileRequests: intl.formatMessage({ id: 'configurationHistory.types.mobileRequests' }),
      waitTime: intl.formatMessage({ id: 'configurationHistory.types.waitTime' }),
      assistedAssignment: intl.formatMessage({ id: 'configurationHistory.types.assistedAssignment' }),
    }
    return (Object.entries(stats.switchesByConfigType) as Array<[ConfigurationType, number]>).map(([type, count]) => ({
      type,
      label: typeLabels[type],
      count,
      color: CONFIG_TYPE_COLORS[type],
    }))
  }, [stats.switchesByConfigType, intl])

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">
        <FormattedMessage id="allCentrals.switchesByType" />
      </h3>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} layout="vertical" margin={{ left: 100, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
          <XAxis type="number" />
          <YAxis type="category" dataKey="label" tick={{ fontSize: 12 }} width={100} />
          <Tooltip />
          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
            {data.map((entry, index) => (
              <Cell key={index} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// Bar chart for switches by central
const SwitchesByCentralChart: FC<{ stats: AllCentralsConfigurationHistoryStats }> = ({ stats }) => {
  const data = useMemo(
    () =>
      stats.switchesByRegion.slice(0, 10).map((region) => ({
        regionName: region.regionName,
        count: region.count,
      })),
    [stats.switchesByRegion]
  )

  if (data.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          <FormattedMessage id="allCentrals.switchesByCentral" />
        </h3>
        <p className="text-sm text-gray-500">
          <FormattedMessage id="configurationHistory.noData" />
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">
        <FormattedMessage id="allCentrals.switchesByCentral" />
      </h3>
      <ResponsiveContainer width="100%" height={Math.max(200, data.length * 30)}>
        <BarChart data={data} layout="vertical" margin={{ left: 120, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
          <XAxis type="number" />
          <YAxis type="category" dataKey="regionName" tick={{ fontSize: 12 }} width={120} />
          <Tooltip />
          <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// Time heatmap component using Recharts
const TimeHeatmap: FC<{ stats: AllCentralsConfigurationHistoryStats }> = ({ stats }) => {
  const maxCount = useMemo(() => Math.max(...stats.timeHeatmap.map((b) => b.count), 1), [stats.timeHeatmap])

  // Prepare data for Recharts - only show every 2nd label to avoid crowding
  const data = useMemo(
    () =>
      stats.timeHeatmap.map((bucket, index) => ({
        ...bucket,
        displayLabel: index % 4 === 0 ? bucket.label : '',
      })),
    [stats.timeHeatmap]
  )

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">
        <FormattedMessage id="allCentrals.timeHeatmap" />
      </h3>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="displayLabel"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            interval={0}
          />
          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} width={30} />
          <Tooltip
            formatter={(value: number) => [value, 'switches']}
            labelFormatter={(_, payload) => {
              if (payload && payload.length > 0) {
                const item = payload[0].payload as { label: string }
                return item.label
              }
              return ''
            }}
          />
          <Bar dataKey="count" radius={[2, 2, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={index} fill={getHeatmapColor(entry.count, maxCount)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-2 flex items-center justify-end gap-2 text-xs text-gray-500">
        <span>
          <FormattedMessage id="allCentrals.less" />
        </span>
        {HEATMAP_COLORS.map((color, i) => (
          <div key={i} className="h-3 w-3 rounded-sm" style={{ backgroundColor: color }} />
        ))}
        <span>
          <FormattedMessage id="allCentrals.more" />
        </span>
      </div>
    </div>
  )
}

// Live activity feed component
const LiveActivityFeed: FC<{
  events: AllCentralsConfigurationHistoryEvent[]
  regionNames: Record<string, string>
}> = ({ events, regionNames }) => {
  const intl = useIntl()

  // Sort events by timestamp descending (most recent first)
  const sortedEvents = useMemo(
    () =>
      [...events]
        .filter((e) => e.previousValue !== e.newValue)
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()),
    [events]
  )

  const getValueLabel = (type: string, value: string | boolean): string => {
    if (type === 'mobileRequests' || type === 'assistedAssignment') {
      return intl.formatMessage({
        id:
          value === true || value === 'true'
            ? 'configurationHistory.value.enabled'
            : 'configurationHistory.value.disabled',
      })
    }
    const messageIdMap: Record<string, Record<string, string>> = {
      assignment: {
        manual: 'configurationHistory.value.assignment.manual',
        trf: 'configurationHistory.value.assignment.trf',
        solver: 'configurationHistory.value.assignment.solver',
        legacy: 'configurationHistory.value.assignment.legacy',
      },
      waitTime: {
        default: 'configurationHistory.value.waitTime.default',
        increased: 'configurationHistory.value.waitTime.increased',
      },
    }
    const messageId = messageIdMap[type]?.[String(value)]
    return messageId
      ? intl.formatMessage({ id: messageId as Parameters<typeof intl.formatMessage>[0]['id'] })
      : String(value)
  }

  if (sortedEvents.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          <FormattedMessage id="allCentrals.activityFeed" />
        </h3>
        <p className="text-sm text-gray-500">
          <FormattedMessage id="configurationHistory.noEvents" />
        </p>
      </div>
    )
  }

  const maxDisplayed = 50

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">
        <FormattedMessage id="allCentrals.activityFeed" />
      </h3>
      <div className="max-h-96 space-y-2 overflow-y-auto">
        {sortedEvents.slice(0, maxDisplayed).map((event) => {
          const typeLabel = intl.formatMessage({ id: `configurationHistory.types.${event.type}` })
          const color = CONFIG_TYPE_COLORS[event.type]

          return (
            <div key={event._id} className="flex items-start gap-3 border-b border-gray-100 pb-2 last:border-b-0">
              <div className="text-sm font-medium whitespace-nowrap text-gray-600">
                {dayjs(event.timestamp).format('HH:mm')}
              </div>
              <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-gray-900">
                  {regionNames[event.regionId] ?? event.regionId}
                </div>
                <div className="text-sm text-gray-500">
                  {typeLabel}: {getValueLabel(event.type, event.previousValue)} →{' '}
                  {getValueLabel(event.type, event.newValue)}
                </div>
              </div>
              <div className="shrink-0 text-xs text-gray-400">{event.changedBy.username}</div>
            </div>
          )
        })}
      </div>
      {sortedEvents.length > maxDisplayed && (
        <div className="mt-2 text-center text-xs text-gray-400">
          <FormattedMessage
            id="allCentrals.showingOfTotal"
            values={{ shown: maxDisplayed, total: sortedEvents.length }}
          />
        </div>
      )}
    </div>
  )
}

export const AllCentralsConfigurationHistoryView: FC<AllCentralsConfigurationHistoryViewProps> = ({
  availableRegions,
  availableDates,
  selectedOperatingDate,
  stats,
  events,
  regionNames,
  isLoading,
  isLive,
  error,
  selectedSection,
  onSectionChange,
  onRegionChange,
  onDateChange,
  onBackToPiloteClicked,
}) => {
  const intl = useIntl()

  return (
    <div className="flex h-full flex-col">
      <AdminHeader
        title={intl.formatMessage({ id: 'configurationHistory.title' })}
        titleClassName="text-blue-300"
        onBackToPiloteClicked={onBackToPiloteClicked}
      />

      <div className="flex-1 overflow-auto p-4 pl-16">
        {/* Section Tabs */}
        <div className="mb-6 flex gap-2">
          <button
            type="button"
            onClick={() => {
              onSectionChange('requests')
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              selectedSection === 'requests' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            <FormattedMessage id="stats.section.requests" />
          </button>
          <button
            type="button"
            onClick={() => {
              onSectionChange('configuration')
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              selectedSection === 'configuration'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            <FormattedMessage id="stats.section.configuration" />
          </button>
        </div>

        {/* Selection Controls */}
        <div className="mb-6 flex flex-wrap items-start gap-4">
          {/* Region Dropdown */}
          <div className="w-64">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              <FormattedMessage id="configurationHistory.region" />
            </label>
            <Select
              value={ALL_CENTRALS_REGION_ID}
              onValueChange={(value) => {
                onRegionChange(value || undefined)
              }}>
              <SelectTrigger aria-label={intl.formatMessage({ id: 'configurationHistory.region' })}>
                <SelectValue placeholder={intl.formatMessage({ id: 'configurationHistory.selectRegion' })} />
              </SelectTrigger>
              <SelectContent>
                {availableRegions.map((region) => (
                  <SelectItem key={region.regionId} value={region.regionId}>
                    {region.regionName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Evening Dropdown */}
          <div className="w-80">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              <FormattedMessage id="configurationHistory.evening" />
            </label>
            <Select
              value={selectedOperatingDate ?? ''}
              onValueChange={(value) => {
                onDateChange(value || undefined)
              }}
              disabled={availableDates.length === 0}>
              <SelectTrigger aria-label={intl.formatMessage({ id: 'configurationHistory.evening' })}>
                <SelectValue placeholder={intl.formatMessage({ id: 'configurationHistory.selectDate' })} />
              </SelectTrigger>
              <SelectContent>
                {availableDates.map((date) => (
                  <SelectItem key={date.date} value={date.date}>
                    {date.label}
                  </SelectItem>
                ))}
                {availableDates.length === 0 && (
                  <div className="px-4 py-2 text-gray-500">
                    <FormattedMessage id="configurationHistory.noEvening" />
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Content */}
        {!selectedOperatingDate ? (
          <div className="flex h-96 items-center justify-center text-gray-500">
            <FormattedMessage id="configurationHistory.selectDatePrompt" />
          </div>
        ) : error ? (
          <div className="flex h-96 flex-col items-center justify-center gap-2">
            <div className="text-lg font-medium text-red-600">
              <FormattedMessage id="configurationHistory.error" />
            </div>
            <div className="text-sm text-gray-500">{error.message}</div>
          </div>
        ) : isLoading ? (
          <div className="flex h-96 items-center justify-center text-gray-500">
            <FormattedMessage id="configurationHistory.loading" />
          </div>
        ) : isLive ? (
          // Live mode: show activity feed
          <div className="space-y-6">
            <SummaryCards stats={stats} />
            <LiveActivityFeed events={events} regionNames={regionNames} />
          </div>
        ) : (
          // Historical mode: show aggregate stats
          <div className="space-y-6">
            <SummaryCards stats={stats} />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <SwitchesByTypeChart stats={stats} />
              <SwitchesByCentralChart stats={stats} />
            </div>
            <TimeHeatmap stats={stats} />
            <LiveActivityFeed events={events} regionNames={regionNames} />
          </div>
        )}
      </div>
    </div>
  )
}
